/**
 * B168 (TRA-202) — the additive-evolution wall. Response schemas are
 * `.strict()` ON PURPOSE server-side: a handler emitting a key the
 * contract never declared must fail its own tests. But a deployed app
 * bundle parses with the contract it was BUILT with, so a strict parse
 * in the CLIENT turns every additive amendment (B145/B155's order-line
 * display fields) into a raw zod refusal on a live operator screen.
 *
 * `tolerantResponse` is the one derivation clients parse responses with
 * instead: structurally identical to the declared schema, except every
 * object STRIPS unknown keys rather than refusing them — at every
 * depth, through arrays, optionals, unions, records, tuples, effects
 * and lazies. Type walls are untouched: a wrong-typed declared key
 * refuses exactly as before. Unknown keys are stripped, never carried,
 * so nothing downstream can come to depend on an undeclared field.
 */
import { z } from "zod";

const memo = new WeakMap<z.ZodTypeAny, z.ZodTypeAny>();

/** Derive (memoized) the additive-tolerant twin of a response schema. */
export function tolerantResponse(schema: z.ZodTypeAny): z.ZodTypeAny {
  const hit = memo.get(schema);
  if (hit !== undefined) return hit;
  const derived = walk(schema);
  memo.set(schema, derived);
  return derived;
}

function walk(schema: z.ZodTypeAny): z.ZodTypeAny {
  const def = (schema as { _def?: { typeName?: string } })._def as any;
  switch (def?.typeName) {
    case "ZodObject": {
      const shape = (schema as z.AnyZodObject).shape as Record<string, z.ZodTypeAny>;
      const nextShape: Record<string, z.ZodTypeAny> = {};
      for (const [key, value] of Object.entries(shape)) nextShape[key] = walk(value);
      // Rebuild via the constructor so checks/description survive; the ONLY
      // change is unknownKeys strict -> strip.
      return new z.ZodObject({ ...def, shape: () => nextShape, unknownKeys: "strip" });
    }
    case "ZodArray":
      return new z.ZodArray({ ...def, type: walk(def.type) });
    case "ZodOptional":
      return new z.ZodOptional({ ...def, innerType: walk(def.innerType) });
    case "ZodNullable":
      return new z.ZodNullable({ ...def, innerType: walk(def.innerType) });
    case "ZodDefault":
      return new z.ZodDefault({ ...def, innerType: walk(def.innerType) });
    case "ZodUnion":
      return new z.ZodUnion({ ...def, options: def.options.map((o: z.ZodTypeAny) => walk(o)) });
    case "ZodDiscriminatedUnion":
      return z.discriminatedUnion(def.discriminator, def.options.map((o: z.AnyZodObject) => walk(o)) as never);
    case "ZodIntersection":
      return new z.ZodIntersection({ ...def, left: walk(def.left), right: walk(def.right) });
    case "ZodTuple":
      return new z.ZodTuple({ ...def, items: def.items.map((i: z.ZodTypeAny) => walk(i)), rest: def.rest ? walk(def.rest) : null });
    case "ZodRecord":
      return new z.ZodRecord({ ...def, valueType: walk(def.valueType) });
    case "ZodMap":
      return new z.ZodMap({ ...def, keyType: walk(def.keyType), valueType: walk(def.valueType) });
    case "ZodEffects":
      return new z.ZodEffects({ ...def, schema: walk(def.schema) });
    case "ZodLazy":
      return z.lazy(() => walk(def.getter()));
    default:
      // Leaves (strings, numbers, enums, literals, unknown, …) carry their
      // own checks and no children — returned as-is.
      return schema;
  }
}
